/**
 * External dependencies
 */
import * as React from 'react';
import { useCallback, useRef } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import {
  type TextBorderLineCap,
  TextBorderStyleType,
  type DashedTextBorderStyle,
  type SolidTextBorderStyle,
  type TextBorderStyle,
} from '@src/web-stories-wp/story-editor/src/elements/types';
import {
  DropDown,
  NumericInput,
  PLACEMENT,
  THEME_CONSTANTS,
} from '@web-stories-wp/design-system';
import { MULTIPLE_DISPLAY_VALUE } from '@src/web-stories-wp/story-editor/src/constants';

/**
 * Internal dependencies
 */
import {
  isMultipleValue,
  Label,
  type MultipleValue,
  SettingsRow,
  SettingsRowInputWrapper,
  Space,
  TEXT_BORDER_SETTINGS_POPUP_ID,
  TEXT_BORDER_STYLE_OPTIONS,
} from './shared';
import { inputContainerStyleOverride } from '../../shared';
import { Row } from '../../../form';
import { TextBorderStrokeLineCapSettings } from './textBorderStrokeLineCap';

type TextBorderStrokeStyleSettingsProps = {
  lineCap: TextBorderLineCap | MultipleValue;
  handleChange: (
    property: 'lineCap' | 'style',
    value: TextBorderLineCap | TextBorderStyle
  ) => void;
  style: TextBorderStyle | MultipleValue;
};

const DEFAULT_SOLID_TEXT_BORDER_STYLE: SolidTextBorderStyle = {
  type: TextBorderStyleType.Solid,
};

const DEFAULT_DASHED_TEXT_BORDER_STYLE: DashedTextBorderStyle = {
  type: TextBorderStyleType.Dashed,
  dashLength: 2,
  gapLength: 2,
};

export function TextBorderStrokeStyleSettings({
  handleChange,
  lineCap,
  style,
}: TextBorderStrokeStyleSettingsProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleStyleChange = useCallback(
    (value: TextBorderStyleType) => {
      if (value === TextBorderStyleType.Solid) {
        handleChange('style', DEFAULT_SOLID_TEXT_BORDER_STYLE);
      } else {
        handleChange('style', DEFAULT_DASHED_TEXT_BORDER_STYLE);
      }
    },
    [handleChange]
  );

  const handleDashedBorderChange = useCallback(
    (property: 'dashLength' | 'gapLength', value: number) => {
      handleChange('style', {
        ...(isDashedTextBorderStyle(style)
          ? style
          : DEFAULT_DASHED_TEXT_BORDER_STYLE),
        [property]: value,
      });
    },
    [handleChange, style]
  );

  return (
    <>
      <SettingsRow>
        <Label size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
          {__('Style', 'web-stories')}
        </Label>
        <SettingsRowInputWrapper>
          <DropDown
            ariaLabel={__('Text border style chooser', 'web-stories')}
            ref={dropdownRef}
            onMenuItemClick={(event, value) => handleStyleChange(value)}
            options={TEXT_BORDER_STYLE_OPTIONS}
            placement={PLACEMENT.BOTTOM}
            placeholder={
              isMultipleValue(style)
                ? MULTIPLE_DISPLAY_VALUE
                : __('Solid', 'web-stories')
            }
            popupId={TEXT_BORDER_SETTINGS_POPUP_ID}
            selectedValue={isMultipleValue(style) ? '' : style.type}
          />
        </SettingsRowInputWrapper>
      </SettingsRow>
      {isDashedTextBorderStyle(style) && (
        <>
          <TextBorderStrokeLineCapSettings
            lineCap={lineCap}
            handleChange={(newValue) => handleChange('lineCap', newValue)}
          />
          <Row>
            <NumericInput
              aria-label={__('Dash length', 'web-stories')}
              allowEmpty={false}
              isFloat
              value={style.dashLength}
              onChange={(event, value) => {
                handleDashedBorderChange('dashLength', value);
              }}
              onBlur={(event, value) => {
                handleDashedBorderChange('dashLength', value);
              }}
              suffix={__('Dash', 'web-stories')}
              containerStyleOverride={inputContainerStyleOverride}
            />
            <Space />
            <NumericInput
              aria-label={__('Gap length', 'web-stories')}
              allowEmpty={false}
              isFloat
              value={style.gapLength}
              onChange={(event, value) => {
                handleDashedBorderChange('gapLength', value);
              }}
              onBlur={(event, value) => {
                handleDashedBorderChange('gapLength', value);
              }}
              suffix={__('Gap', 'web-stories')}
              containerStyleOverride={inputContainerStyleOverride}
            />
          </Row>
        </>
      )}
    </>
  );
}

function isDashedTextBorderStyle(
  style: TextBorderStyle | MultipleValue
): style is DashedTextBorderStyle {
  return typeof style === 'object' && style.type === TextBorderStyleType.Dashed;
}
